import { useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import { useLocalization } from '@/context/localization';

export type MessageType<T> = Record<keyof T, (params?: Record<string, string>) => string>;

export const useMessages = <T extends Record<string, object>>(messages: T): MessageType<T> => {
  const { formatMessage } = useIntl();
  const {
    state: { customTranslations },
  } = useLocalization();

  return useMemo(() => {
    const keys = Object.keys(messages) as Array<keyof T>;

    const formattedMessages: Partial<Record<keyof T, (params?: Record<string, string>) => string>> =
      {};

    for (const key of keys) {
      // use BE custom translations if available, otherwise use default translations
      formattedMessages[key] = (params?: Record<string, string>) => {
        // EWM-1026: always hand react-intl a values object, never `undefined`. With no values
        // argument at all it short-circuits before the ICU formatter runs - it returns the raw
        // pattern and reports nothing - so `editAssumptions()` on 'Assumptions ({count})' shipped
        // the literal placeholder to a customer in complete silence (EWM-1010). An empty object
        // takes the formatting path, which raises FORMAT_ERROR that `intlOnError` turns into a
        // hard failure in dev and test. A message with no placeholders is unaffected.
        const values = params ?? {};

        return customTranslations[(messages[key] as MessageDescriptor)?.id ?? '']
          ? // EWM-1091: a BE custom translation is authored in the rich-text editor, so it can
            // carry any tag that editor emits - <p>, <strong>, <em>, <u>, <s>, <h1>-<h3>,
            // <ol>/<ul>/<li>, <a>. react-intl parses each of those as an ICU tag element and
            // raises FORMAT_ERROR ("context variable was not provided") unless `values` holds a
            // formatter for that exact tag, which intlOnError turns into a hard failure. There
            // is nothing to format: SanitizedHtml renders this string through
            // dangerouslySetInnerHTML, so the markup has to survive as literal text. ignoreTag
            // turns tag parsing off for this message and leaves ICU placeholders working, which
            // covers every tag rather than the one that happened to be reported.
            formatMessage(
              {
                id: (messages[key] as MessageDescriptor)?.id ?? '',
                defaultMessage: customTranslations[(messages[key] as MessageDescriptor)?.id ?? ''],
              } as MessageDescriptor,
              values,
              { ignoreTag: true },
            )
          : (messages[key] as MessageDescriptor)?.defaultMessage !== '' // in some cases like for dashboard steps, the defaultMessage for url will be empty
            ? formatMessage(messages[key], values)
            : '';
      };
    }

    return formattedMessages as Record<keyof T, (params?: Record<string, string>) => string>;
  }, [messages, customTranslations, formatMessage]);
};
